import { cn } from "@/components/podkit/lib/cn";
import type { PropsWithChildren, ReactNode } from "react";
import { InputFieldHint } from "@/components/podkit/forms/InputFieldHint";

type Props = {
    label?: ReactNode;
    id?: string;
    hint?: ReactNode;
    error?: ReactNode;
    className?: string;
    disabled?: boolean;
    "data-testid"?: string;
};
/**
 * When an `id` prop is provided, children are rendered outside the <label> element
 * to avoid nesting interactive elements (which violates HTML semantics).
 * Use this for complex inputs like CopyableInput that contain buttons or other focusable elements.
 */
export const InputField = ({
    label,
    id,
    hint,
    error,
    className,
    children,
    disabled = false,
    "data-testid": testId,
}: PropsWithChildren<Props>) => {
    return (
        <div
            className={cn("group flex flex-col gap-1", className)}
            data-state={error ? "error" : undefined}
            data-testid={testId}
        >
            {label && (
                <label
                    className={cn(
                        "text-base font-normal text-content-primary",
                        { "flex flex-col gap-1": !id },
                        { "opacity-50": disabled },
                    )}
                    htmlFor={id}
                >
                    {label}
                    <span className="font-normal">{!id ? children : undefined}</span>
                </label>
            )}
            {id || !label ? children : undefined}
            {error && (
                <span data-testid="input-error" className="text-sm text-content-destructive animate-in fade-in">
                    {error}
                </span>
            )}
            {hint && <InputFieldHint>{hint}</InputFieldHint>}
        </div>
    );
};
